import { useFormikContext } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { Accept, DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { CropArea, cropImage } from 'shared/utils/image/crop-image';
import { describeFileCountLimit, describeImageFailure, isHeicFile, MAX_SOURCE_BYTES } from 'shared/utils/image/image-messages';
import { ImageCompression, useImageCompression } from 'ud-form/components/image-compression';
import { ImageCropOptions } from 'ud-form/components/image-crop';
import { ImageUpscaleOption } from 'ud-form/components/image-upscale/types';
import { IMAGE_ACCEPT } from '../domain/accepted-formats';
import { DropzoneValueAdapter, listValueAdapter } from '../domain/adapters';
import { filesOf, moveItem, removeItem, toPreviews } from '../domain/previews';
import { DropzoneValue } from '../types';
import { useDropzoneUpscale } from './use-dropzone-upscale';
import { useObjectUrls } from './use-object-urls';

interface UseDropzoneFieldParams extends ImageCropOptions {
  name: string;
  multiple?: boolean;
  maxFiles?: number;
  accept?: Accept;
  maxSize?: number;
  compression?: ImageCompression;
  disabled?: boolean;
  /** Кадрировать выбранное изображение до записи в форму. В multiple-полях игнорируется. */
  crop?: boolean;
  /** Как поле хранит картинки. По умолчанию значение — просто их список. */
  adapter?: DropzoneValueAdapter;
  /**
   * Подсказать, что картинка мелкая, и предложить ИИ-улучшение — и только что выбранной, и
   * сохранённой. В multiple-поле кнопка «Улучшить» встаёт на каждое мелкое превью.
   */
  upscale?: ImageUpscaleOption;
}

/**
 * Всё, что дропзона делает со значением поля: читает, обрабатывает выбранное,
 * пишет обратно.
 *
 * Логика собрана в одном месте намеренно. До объединения она была размазана по
 * трём вариантам дропзоны и одному хуку легаси-версии, и каждый знал свою часть:
 * один умел удалять, другой — сортировать, третий подменял запись в форму своим
 * обработчиком. Из-за этого пропсы доезжали до `react-dropzone` не везде — так,
 * например, поле с одной картинкой молча принимало пачку файлов.
 *
 * Значение поля — всегда список, даже когда картинка одна: в таком виде его
 * читают формы игрока, команды, заявки и локаций, и менять их контракт эта
 * задача не берётся.
 */
export function useDropzoneField(params: UseDropzoneFieldParams) {
  const {
    name,
    multiple = false,
    maxFiles,
    accept = IMAGE_ACCEPT,
    maxSize = MAX_SOURCE_BYTES,
    compression,
    disabled,
    crop = false,
    cropAspect,
    adapter = listValueAdapter,
    upscale,
  } = params;

  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>();
  const value = values[name];
  const items = useMemo(() => adapter.read(value), [adapter, value]);

  const { processFiles, isProcessing } = useImageCompression(compression);

  const files = useMemo(() => filesOf(items), [items]);
  const resolveUrl = useObjectUrls(files);
  const previews = useMemo(() => toPreviews(items, resolveUrl), [items, resolveUrl]);

  /**
   * Сколько картинок помещается.
   *
   * У одиночного поля это единица, и знать её важно: `react-dropzone` отклоняет
   * **всю** пачку, если файлов больше, чем можно, — и без этого числа отказ
   * объяснялся бы как «не удалось прочитать файл», хотя с файлами всё в порядке.
   */
  const limit = multiple ? maxFiles : 1;

  /** Набор укомплектован — дальше зона закрыта, а не молча глотает лишнее. */
  const isFull = multiple && maxFiles !== undefined && items.length >= maxFiles;

  /**
   * Файл ждёт кадрирования: модалка открыта, значение поля ещё прежнее.
   *
   * Кроп встроен здесь, а не в `UDFormDropzone`, потому что поле логотипа
   * собирает `useDropzoneField` и `UDDropzone` само — иначе оно осталось бы без
   * кадрирования, как когда-то оставалось без сжатия.
   */
  const [pendingCrop, setPendingCrop] = useState<File | null>(null);

  /** Кадрируем только одиночные поля: очередь «обрежьте пять фото подряд» — отдельная задача. */
  const isCropEnabled = crop && !multiple;

  /**
   * Довести файл до формы прежним путём: сжатие, затем запись.
   *
   * Сжатие идёт после обрезки, а не до: вырезанный кадр меньше исходника и часто
   * проходит порог сжатия сам — обратный порядок жал бы то, что сейчас выбросят.
   */
  const writeFile = useCallback(
    async (file: File): Promise<File | null> => {
      const processed = await processFiles([file]);
      if (processed.length === 0) return null;

      setFieldValue(name, adapter.write(value, [processed[0]]));
      return processed[0];
    },
    [processFiles, setFieldValue, name, adapter, value],
  );

  const handleDrop = useCallback(
    async (accepted: File[], rejections: FileRejection[], _event: DropEvent) => {
      // Отказ по количеству объясняется здесь: сколько картинок помещается —
      // знает поле, а не обработка изображений.
      const tooMany = rejections.some((rejection) => rejection.errors.some((error) => error.code === 'too-many-files'));

      if (tooMany && limit !== undefined) {
        toast.error(describeFileCountLimit(limit));
        return;
      }

      const rejected = rejections.map((rejection) => ({
        file: rejection.file,
        codes: rejection.errors.map((error) => error.code),
      }));

      // HEIC отсекается до модалки: браузер его не декодирует, и показывать в
      // кропере то, что не отрисуется, нельзя — отказ объяснит общий путь ниже.
      if (isCropEnabled && accepted.length > 0 && !isHeicFile(accepted[0])) {
        // Про отказанные файлы говорим и здесь: пригодный среди них нашёлся, но
        // промолчать про остальные — это не то, что делает поле без кропа.
        if (rejected.length > 0) await processFiles([], rejected);

        setPendingCrop(accepted[0]);
        return;
      }

      const processed = await processFiles(accepted, rejected);

      // Ни одного пригодного файла — прежнее значение не трогаем: обнулять уже
      // выбранную картинку из-за неудачной попытки заменить её нечестно.
      if (processed.length === 0) return;

      setFieldValue(name, adapter.write(value, multiple ? [...items, ...processed] : [processed[0]]));
    },
    [processFiles, setFieldValue, name, multiple, items, limit, adapter, value, isCropEnabled],
  );

  /** «Обрезать»: в форму уходит вырезанный кадр. */
  const confirmCrop = useCallback(
    async (area: CropArea) => {
      if (!pendingCrop) return;

      setPendingCrop(null);

      try {
        await writeFile(await cropImage(pendingCrop, area));
      } catch (error) {
        // Обрезать не вышло — прежнее значение поля не трогаем, ровно как при
        // неудачном сжатии: терять уже выбранную картинку из-за этого нечестно.
        toast.error(describeImageFailure(error, pendingCrop));
      }
    },
    [pendingCrop, writeFile],
  );

  /** «Оставить как есть»: исходный файл идёт прежним путём, без перекодирования. */
  const skipCrop = useCallback(() => {
    if (!pendingCrop) return;

    setPendingCrop(null);
    void writeFile(pendingCrop);
  }, [pendingCrop, writeFile]);

  /** «Отмена»: значение поля остаётся прежним. */
  const cancelCrop = useCallback(() => setPendingCrop(null), []);

  const remove = useCallback(
    (index: number) => {
      setFieldValue(name, adapter.write(value, removeItem(items, index)));
    },
    [setFieldValue, name, items, adapter, value],
  );

  const reorder = useCallback(
    (from: number, to: number) => {
      setFieldValue(name, adapter.write(value, moveItem(items, from, to)));
    },
    [setFieldValue, name, items, adapter, value],
  );

  const writeItems = useCallback(
    (next: DropzoneValue[]) => setFieldValue(name, adapter.write(value, next)),
    [setFieldValue, name, adapter, value],
  );

  const { view: upscaleView, isBusy: isUpscaleBusy } = useDropzoneUpscale({
    // Выключенное поле не меняют вовсе — улучшение тоже (у локаций без подписки).
    upscale: disabled ? undefined : upscale,
    multiple,
    items,
    previews,
    processFiles,
    writeItems,
  });

  // Пока открыта модалка кадрирования или улучшения, зона закрыта: второй файл поверх
  // первого подменил бы то, что человек сейчас кадрирует или сравнивает.
  const isBusy = Boolean(disabled) || isProcessing || isFull || pendingCrop !== null || isUpscaleBusy;

  const { getRootProps, getInputProps, isDragActive, isFocused } = useDropzone({
    accept,
    maxSize,
    multiple,
    // `maxFiles` в `react-dropzone` считает файлы одного выбора, а ограничение у
    // нас на весь набор — поэтому передаётся остаток.
    maxFiles: multiple && maxFiles !== undefined ? Math.max(maxFiles - items.length, 0) : undefined,
    disabled: isBusy,
    onDrop: handleDrop,
  });

  return {
    items: items as DropzoneValue[],
    previews,
    rootProps: getRootProps(),
    inputProps: getInputProps(),
    isDragActive,
    isFocused,
    isProcessing,
    isFull,
    isDisabled: isBusy,
    remove,
    reorder,
    /** Файл, который сейчас кадрируют: им же управляется модалка. */
    cropFile: pendingCrop,
    cropAspect,
    confirmCrop,
    skipCrop,
    cancelCrop,
    /** Подсказка «мелкая» и модалка улучшения; `undefined` — поле без `upscale`. */
    upscale: upscaleView,
  };
}
